import { type DependencyList, type RefObject,useEffect, useRef } from "react";

import { LOOP_ROOT_MARGIN, prefersReducedMotion } from "./anime";

/**
 * Paint one frame. `w`/`h` are the canvas's CSS-pixel size (the context is already
 * scaled for `devicePixelRatio`); `t` is the raw frame count since mount, scenes
 * apply their own time scale (`t * 0.005` and friends) inside the draw.
 */
export type CanvasDraw = (ctx: CanvasRenderingContext2D, w: number, h: number, t: number) => void;

type CanvasLoopOptions = {
  /** The `t` a still render shows: reduced motion, and the first frame before the loop has run. */
  still: number;
  /** Cap on `devicePixelRatio` for the backing store, the bento backdrops stop at 2. */
  maxDpr?: number;
  /** Cap on paints per second for an ambient scene; `t` still counts 60fps frames, so it keeps its speed. */
  fps?: number;
};

/**
 * The raw-canvas sibling of `useLoopInView`, for scenes that render by hand with
 * `requestAnimationFrame` instead of an anime timeline (anime.js stays the site's
 * only animation *library*). Owns the whole lifecycle those scenes used to
 * duplicate: dpr-aware sizing against the canvas's parent, a `ResizeObserver`
 * that keeps a valid frame after any resize, the same in-view play/pause gate as
 * `useLoopInView`, a single held frame under `prefers-reduced-motion`, and full
 * teardown on unmount.
 */
export function useCanvasLoop(
  draw: CanvasDraw,
  { still, maxDpr = Infinity, fps }: CanvasLoopOptions,
  deps: DependencyList = [],
): RefObject<HTMLCanvasElement | null> {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !parent || !ctx) return;

    let w = 0;
    let h = 0;
    let raf = 0;
    let wait = 0;
    let t = 0;
    // the frame a paused (off-screen) repaint shows, the last one drawn, once the loop has run
    let hold = still;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
      const rect = parent.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const paint = (frame: number) => {
      ctx.clearRect(0, 0, w, h);
      draw(ctx, w, h, frame);
    };

    resize();
    const ro = new ResizeObserver(() => {
      resize();
      // setting the backing store blanked the canvas: repaint the held frame at once,
      // paused or not (a capped loop's next paint can be a few frames off); the
      // observer also fires once on observe, so this is the first paint too
      paint(hold);
    });
    ro.observe(parent);

    // reduced motion: one static frame, no loop, the observer above keeps it valid
    if (prefersReducedMotion()) {
      paint(still);
      return () => ro.disconnect();
    }

    // A capped scene sleeps through its gap on a timer, so the frames it skips run
    // no rendering lifecycle at all: it wakes half a 60Hz frame early and asks for
    // the frame that ends the gap (the check, with 2ms of slack for rAF jitter,
    // holds it on faster displays); `t` steps over the skipped frames.
    const gap = fps ? 1000 / fps - 2 : 0;
    const step = fps ? 60 / fps : 1;
    let last = -Infinity;
    const frame = () => (raf = requestAnimationFrame(tick));
    const tick = (now: number) => {
      if (now - last < gap) return void frame();
      last = now;
      paint(t);
      hold = t;
      t += step;
      if (fps) wait = window.setTimeout(frame, gap - 8);
      else frame();
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      clearTimeout(wait);
      raf = 0;
    };

    // only spin the loop while the scene is on screen
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!raf) frame();
          } else if (raf) stop();
        }
      },
      { rootMargin: LOOP_ROOT_MARGIN },
    );
    io.observe(parent);

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
